import React, { useState, useEffect, useRef } from 'react'

/**
 * Admin Mode persistence
 *
 * - Enable Admin Mode on localhost, or by adding `?admin=1` to the URL.
 * - Enter a token in the Admin Mode bar (stored in `localStorage` as `cluso_admin_token`).
 * - Each toggle PATCHes `/.netlify/functions/feature-comparison` immediately.
 *   Server-side writes are guarded by `process.env.ADMIN_TOKEN`.
 */

type FeatureStatus = 'yes' | 'no' | 'partial' | 'hidden'

interface FeatureData {
  others: FeatureStatus
  cluso: FeatureStatus
}

interface FeatureSettings {
  features: Record<string, FeatureData>
}

interface FeatureRow {
  id: string
  label: string
  category?: string
}

const featureRows: FeatureRow[] = [
  // Getting Started
  { id: 'getting-started', label: 'Getting Started', category: 'header' },
  { id: 'no-login-required', label: 'No login required' },
  { id: 'free-for-life-edition', label: 'Free for life edition' },
  { id: 'pro-edition', label: 'Pro edition' },
  { id: 'pro-cloud-features', label: 'Pro cloud features' },

  // Element Selection & Context
  { id: 'element-selection', label: 'Element Selection & Context *', category: 'header' },
  { id: 'select-elements-as-context-chips', label: 'Select elements as context "chips"' },
  { id: 'capture-screenshots-with-context-chips', label: 'Capture screenshots with context chips' },
  { id: 'move-elements-visually-with-context', label: 'Move elements visually with context' },
  { id: 'source-file-location-mapping', label: 'Source file location mapping' },

  // Console & Debugging
  { id: 'console-debugging', label: 'Console & Debugging *', category: 'header' },
  { id: 'show-web-console-in-app', label: 'Show web console in-app' },
  { id: 'select-console-entries-as-context', label: 'Select console entries as context' },
  { id: 'pre-fetch-error-solutions-automatically', label: 'Pre-fetch error solutions automatically' },

  // Search & Indexing
  { id: 'search-indexing', label: 'Search & Indexing *', category: 'header' },
  { id: 'instant-file-search-with-local-mgrep-indexing', label: 'Instant file search with local mgrep indexing' },
  { id: 'semantic-code-search-with-embeddings', label: 'Semantic code search with embeddings' },
  { id: 'auto-updating-indexes-on-file-changes', label: 'Auto-updating indexes on file changes' },

  // Code Editing & Updates
  { id: 'code-editing', label: 'Code Editing & Updates *', category: 'header' },
  { id: 'instant-code-updates-with-specialized-local-models', label: 'Instant code updates with specialized local models' },
  { id: 'live-dom-css-patching-with-preview', label: 'Live DOM/CSS patching with preview' },
  { id: 'patch-approval-workflow-accept-reject', label: 'Patch approval workflow (accept/reject)' },
  { id: 'backups-and-recovery', label: 'Backups and recovery' },

  // Voice Control
  { id: 'voice-control', label: 'Voice Control *', category: 'header' },
  { id: 'voice-select-elements', label: 'Voice select elements' },
  { id: 'voice-changes-and-approvals', label: 'Voice changes and approvals' },
  { id: 'real-time-voice-streaming-gemini-live', label: 'Real-time voice streaming (Gemini Live)' },
  { id: 'voice-session-logs-learnings', label: 'Voice session logs & learnings' },

  // MCP & Advanced Tooling
  { id: 'mcp-tooling', label: 'MCP & Advanced Tooling *', category: 'header' },
  { id: 'smart-mcp-tooling', label: 'Smart MCP tooling' },
  { id: 'mcp-host-server-integration', label: 'MCP host/server integration' },
  { id: 'lsp-integration-hover-completion-go-to-def', label: 'LSP integration (hover, completion, go-to-def)' },

  // AI Models & Collaboration
  { id: 'ai-models', label: 'AI Models & Collaboration *', category: 'header' },
  { id: 'multi-model-support-claude-gemini-gpt', label: 'Multi-model support (Claude, Gemini, GPT)' },
  { id: 'fast-orchestration-model', label: 'Fast orchestration model' },
  { id: 'context-optimization', label: 'Context optimization' },
  { id: 'knowledge-base', label: 'Knowledge base' },
  { id: 'adaptive-learning', label: 'Adaptive learning' },
  { id: 'steering-questions', label: 'Steering questions' },
  { id: 'multi-instance-collaboration', label: 'Multi-instance collaboration' },
  { id: 'multi-window-project-support', label: 'Multi-window project support' },

  // Project & File Management
  { id: 'project-management', label: 'Project & File Management *', category: 'header' },
  { id: 'built-in-file-explorer-with-autocomplete', label: 'Built-in file explorer with autocomplete' },
  { id: 'git-integration-branch-commit-stash', label: 'Git integration (branch, commit, stash)' },
  { id: 'kanban-board-todo-list', label: 'Kanban board & todo list' },
  { id: 'device-preview-presets-iphone-ipad-etc', label: 'Device preview presets (iPhone, iPad, etc.)' },

  // Privacy & Data
  { id: 'privacy-data', label: 'Privacy & Data *', category: 'header' },
  { id: '100-local-processing-option', label: '100% local processing option' },
  { id: 'bring-your-own-api-keys', label: 'Bring your own API keys' },
  { id: 'no-data-leaves-your-machine-byok-mode', label: 'No data leaves your machine (BYOK mode)' },
]

const defaultFeatures: Record<string, FeatureData> = {
  'no-login-required': { others: 'no', cluso: 'yes' },
  'free-for-life-edition': { others: 'no', cluso: 'yes' },
  'pro-edition': { others: 'yes', cluso: 'yes' },
  'pro-cloud-features': { others: 'partial', cluso: 'yes' },
  'select-elements-as-context-chips': { others: 'partial', cluso: 'yes' },
  'capture-screenshots-with-context-chips': { others: 'partial', cluso: 'yes' },
  'move-elements-visually-with-context': { others: 'partial', cluso: 'yes' },
  'source-file-location-mapping': { others: 'no', cluso: 'yes' },
  'show-web-console-in-app': { others: 'no', cluso: 'yes' },
  'select-console-entries-as-context': { others: 'partial', cluso: 'yes' },
  'pre-fetch-error-solutions-automatically': { others: 'no', cluso: 'yes' },
  'instant-file-search-with-local-mgrep-indexing': { others: 'no', cluso: 'yes' },
  'semantic-code-search-with-embeddings': { others: 'no', cluso: 'yes' },
  'auto-updating-indexes-on-file-changes': { others: 'no', cluso: 'yes' },
  'instant-code-updates-with-specialized-local-models': { others: 'no', cluso: 'yes' },
  'live-dom-css-patching-with-preview': { others: 'partial', cluso: 'yes' },
  'patch-approval-workflow-accept-reject': { others: 'partial', cluso: 'yes' },
  'backups-and-recovery': { others: 'no', cluso: 'yes' },
  'voice-select-elements': { others: 'no', cluso: 'yes' },
  'voice-changes-and-approvals': { others: 'no', cluso: 'yes' },
  'real-time-voice-streaming-gemini-live': { others: 'no', cluso: 'yes' },
  'voice-session-logs-learnings': { others: 'no', cluso: 'yes' },
  'smart-mcp-tooling': { others: 'no', cluso: 'yes' },
  'mcp-host-server-integration': { others: 'no', cluso: 'yes' },
  'lsp-integration-hover-completion-go-to-def': { others: 'partial', cluso: 'yes' },
  'multi-model-support-claude-gemini-gpt': { others: 'partial', cluso: 'yes' },
  'fast-orchestration-model': { others: 'no', cluso: 'yes' },
  'context-optimization': { others: 'no', cluso: 'yes' },
  'knowledge-base': { others: 'no', cluso: 'yes' },
  'adaptive-learning': { others: 'no', cluso: 'yes' },
  'steering-questions': { others: 'no', cluso: 'yes' },
  'multi-instance-collaboration': { others: 'no', cluso: 'yes' },
  'multi-window-project-support': { others: 'no', cluso: 'yes' },
  'built-in-file-explorer-with-autocomplete': { others: 'partial', cluso: 'yes' },
  'git-integration-branch-commit-stash': { others: 'partial', cluso: 'yes' },
  'kanban-board-todo-list': { others: 'no', cluso: 'yes' },
  'device-preview-presets-iphone-ipad-etc': { others: 'partial', cluso: 'yes' },
  '100-local-processing-option': { others: 'no', cluso: 'yes' },
  'bring-your-own-api-keys': { others: 'partial', cluso: 'yes' },
  'no-data-leaves-your-machine-byok-mode': { others: 'no', cluso: 'yes' },
}

const statusOrder: FeatureStatus[] = ['yes', 'no', 'partial', 'hidden']

const CheckIcon = () => (
  <span className="check-icon">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  </span>
)

const XIcon = () => (
  <span className="x-icon">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <path d="M18 6L6 18M6 6l12 12"/>
    </svg>
  </span>
)

const PartialIcon = () => (
  <span className="partial-icon">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <path d="M5 12h14"/>
    </svg>
  </span>
)

const HiddenIcon = () => (
  <span className="hidden-icon" style={{
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'rgba(100, 100, 100, 0.15)',
    color: '#666',
  }}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  </span>
)

const StatusIcon: React.FC<{ status: FeatureStatus }> = ({ status }) => {
  switch (status) {
    case 'yes': return <CheckIcon />
    case 'no': return <XIcon />
    case 'partial': return <PartialIcon />
    case 'hidden': return <HiddenIcon />
  }
}

export const FeatureComparison: React.FC = () => {
  const [features, setFeatures] = useState<Record<string, FeatureData>>(defaultFeatures)
  const [isAdminMode, setIsAdminMode] = useState(false)
  const [adminTokenInput, setAdminTokenInput] = useState('')
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  const featuresRef = useRef(features)
  useEffect(() => {
    featuresRef.current = features
  }, [features])

  const adminTokenRef = useRef<string>('')

  const getStoredAdminToken = (): string => {
    try {
      return localStorage.getItem('cluso_admin_token') || ''
    } catch {
      return ''
    }
  }

  const setStoredAdminToken = (token: string) => {
    try {
      if (!token) {
        localStorage.removeItem('cluso_admin_token')
      } else {
        localStorage.setItem('cluso_admin_token', token)
      }
    } catch {
      // ignore
    }
    adminTokenRef.current = token
  }

  // Admin mode is enabled on localhost, or via `?admin=1`.
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    const params = new URLSearchParams(window.location.search)
    const isAdminQuery = params.get('admin') === '1'
    setIsAdminMode(isLocalhost || isAdminQuery)

    const token = getStoredAdminToken()
    adminTokenRef.current = token
    setAdminTokenInput(token)
  }, [])

  // Load settings (prefer the Netlify Function, fallback to static JSON).
  useEffect(() => {
    const load = async () => {
      // 1) Try the persisted store first (Netlify Function)
      try {
        const res = await fetch('/.netlify/functions/feature-comparison', { cache: 'no-store' })
        if (res.ok) {
          const data = (await res.json()) as FeatureSettings
          if (data.features) {
            setFeatures(prev => ({ ...prev, ...data.features }))
            // If we can load from the server, prefer it over any local fallback.
            try {
              localStorage.removeItem('cluso_feature_comparison_overrides')
            } catch {
              // ignore
            }
            return
          }
        }
      } catch {
        // ignore and fall back
      }

      // 2) Fall back to local overrides (useful if you run without Netlify Functions locally)
      try {
        const raw = localStorage.getItem('cluso_feature_comparison_overrides')
        if (raw) {
          const parsed = JSON.parse(raw) as FeatureSettings
          if (parsed.features) {
            setFeatures(prev => ({ ...prev, ...parsed.features }))
            return
          }
        }
      } catch {
        // ignore
      }

      // 3) Fall back to the bundled static JSON
      try {
        const res = await fetch('/feature-comparison.json', { cache: 'no-store' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as FeatureSettings
        if (data.features) {
          setFeatures(prev => ({ ...prev, ...data.features }))
        }
      } catch (err) {
        console.log('Using default feature settings:', err)
      }
    }

    load()
  }, [])

  const persistSettings = async (nextFeatures: Record<string, FeatureData>): Promise<'server' | 'local'> => {
    const data: FeatureSettings = { features: nextFeatures }

    // Local fallback so reloads reflect edits even if you aren't running Netlify Functions.
    try {
      localStorage.setItem('cluso_feature_comparison_overrides', JSON.stringify(data))
    } catch {
      // ignore
    }

    const token = adminTokenRef.current
    if (!token) {
      // No server write without a token; still keep the local fallback.
      return 'local'
    }

    const res = await fetch('/.netlify/functions/feature-comparison', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })

    if (!res.ok) {
      // If you're running Vite without Netlify's function runtime, this will 404.
      // In that case we still keep the local fallback.
      if (res.status === 404) {
        return 'local'
      }
      const text = await res.text().catch(() => '')
      throw new Error(text || `Save failed (HTTP ${res.status})`)
    }

    // Server is now source of truth.
    try {
      localStorage.removeItem('cluso_feature_comparison_overrides')
    } catch {
      // ignore
    }

    return 'server'
  }

  const cycleStatus = (featureId: string, column: 'others' | 'cluso') => {
    if (!isAdminMode) return

    const prev = featuresRef.current
    const current = prev[featureId]?.[column] || 'no'
    const currentIndex = statusOrder.indexOf(current)
    const nextIndex = (currentIndex + 1) % statusOrder.length
    const nextStatus = statusOrder[nextIndex]

    const next: Record<string, FeatureData> = {
      ...prev,
      [featureId]: {
        ...(prev[featureId] || { others: 'no', cluso: 'yes' }),
        [column]: nextStatus,
      },
    }

    // Optimistic UI
    setFeatures(next)
    setSaveStatus('saving')
    setSaveMessage('Saving…')

    persistSettings(next)
      .then((savedTo) => {
        setSaveStatus('saved')
        setSaveMessage(savedTo === 'server' ? 'Saved' : 'Saved locally')
        setTimeout(() => {
          setSaveStatus('idle')
          setSaveMessage(null)
        }, 1500)
      })
      .catch((err: unknown) => {
        console.error('Failed to persist feature settings:', err)
        // Keep the optimistic state + local fallback; just surface the error.
        setSaveStatus('error')
        setSaveMessage(err instanceof Error ? err.message : 'Failed to save')
        setTimeout(() => {
          setSaveStatus('idle')
        }, 4000)
      })
  }

  const getFeatureData = (id: string): FeatureData => {
    return features[id] || { others: 'no', cluso: 'yes' }
  }

  // Filter out hidden rows for non-admin mode
  const visibleRows = featureRows.filter(row => {
    if (row.category === 'header') return true
    const data = getFeatureData(row.id)
    if (!isAdminMode && (data.others === 'hidden' && data.cluso === 'hidden')) {
      return false
    }
    return true
  })

  return (
    <section className="comparison" id="comparison">
      <div className="comparison-container">
        <div className="section-header">
          <p className="section-label">Why Cluso?</p>
          <h2 className="section-title">Built different from the ground up</h2>
          <p className="section-subtitle">
            See how Cluso compares to other inspector tools
          </p>
        </div>

        {isAdminMode && (
          <div style={{
            background: 'rgba(249, 115, 22, 0.1)',
            border: '1px solid var(--accent)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.2rem' }}>🛠️</span>
              <span style={{ fontWeight: 600, color: 'var(--accent)' }}>Admin Mode</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                Click icons to cycle: Yes → No → Partial → Hidden
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="password"
                  placeholder="Admin token"
                  value={adminTokenInput}
                  onChange={(e) => setAdminTokenInput(e.target.value)}
                  style={{
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.85rem',
                    width: '220px',
                  }}
                />
                <button
                  onClick={() => {
                    const trimmed = adminTokenInput.trim()
                    setStoredAdminToken(trimmed)
                    setSaveMessage(trimmed ? 'Token saved' : 'Token cleared')
                    setTimeout(() => setSaveMessage(null), 1500)
                  }}
                  style={{
                    background: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                  }}
                  title="Stored in localStorage as cluso_admin_token"
                >
                  Set token
                </button>
              </div>
              <span style={{
                color: saveStatus === 'error' ? '#ef4444' : saveStatus === 'saved' ? '#22c55e' : 'var(--text-muted)',
                fontSize: '0.85rem',
                minWidth: '80px',
                textAlign: 'right',
              }}>
                {saveMessage || ''}
              </span>
            </div>
          </div>
        )}

        <table className="comparison-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th className="others-header">Other Inspector/s</th>
              <th className="cluso-header">Cluso</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(row => {
              if (row.category === 'header') {
                return (
                  <tr key={row.id} className="category-row">
                    <td colSpan={3}>{row.label}</td>
                  </tr>
                )
              }

              const data = getFeatureData(row.id)
              const showOthers = isAdminMode || data.others !== 'hidden'
              const showCluso = isAdminMode || data.cluso !== 'hidden'

              return (
                <tr key={row.id} style={
                  (data.others === 'hidden' && data.cluso === 'hidden')
                    ? { opacity: 0.4, background: 'rgba(100, 100, 100, 0.1)' }
                    : undefined
                }>
                  <td>{row.label}</td>
                  <td
                    onClick={() => cycleStatus(row.id, 'others')}
                    style={isAdminMode ? { cursor: 'pointer' } : undefined}
                    title={isAdminMode ? `Click to change (current: ${data.others})` : undefined}
                  >
                    {showOthers && <StatusIcon status={data.others} />}
                  </td>
                  <td
                    onClick={() => cycleStatus(row.id, 'cluso')}
                    style={isAdminMode ? { cursor: 'pointer' } : undefined}
                    title={isAdminMode ? `Click to change (current: ${data.cluso})` : undefined}
                  >
                    {showCluso && <StatusIcon status={data.cluso} />}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '1rem', textAlign: 'center' }}>
          * Some features may be Pro only. Final feature tiers TBD.
        </p>
      </div>
    </section>
  )
}

export default FeatureComparison
